import React, { useRef, useEffect, useCallback, useState } from 'react';
import { UPLOAD_URL } from '../config';

const FONT_FAMILIES = [
    { label: 'Default', value: '' },
    { label: 'Arial', value: 'Arial, sans-serif' },
    { label: 'Georgia', value: 'Georgia, serif' },
    { label: 'Times New Roman', value: '"Times New Roman", serif' },
    { label: 'Verdana', value: 'Verdana, sans-serif' },
    { label: 'Courier New', value: '"Courier New", monospace' },
    { label: 'Trebuchet MS', value: '"Trebuchet MS", sans-serif' },
    { label: 'Impact', value: 'Impact, sans-serif' },
];

const FONT_SIZES = [
    { label: 'Small', value: '1' },
    { label: 'Normal', value: '3' },
    { label: 'Large', value: '5' },
    { label: 'X-Large', value: '6' },
    { label: 'XX-Large', value: '7' },
];

interface RichTextEditorProps {
    value: string;
    onChange: (html: string) => void;
    placeholder?: string;
    minHeight?: number;
    /** Folder to upload images into (default: general) */
    imageFolder?: string;
}

// ── Toolbar button ──────────────────────────────────────────────────────────
const Btn: React.FC<{
    title: string;
    onClick: () => void;
    active?: boolean;
    children: React.ReactNode;
}> = ({ title, onClick, active, children }) => (
    <button
        type="button"
        title={title}
        onMouseDown={e => { e.preventDefault(); onClick(); }}
        className={`px-2 py-1 rounded text-sm transition-colors ${active ? 'bg-[#EF5D22] text-white' : 'hover:bg-gray-200 text-gray-700'}`}
    >
        {children}
    </button>
);

// ── Separator ───────────────────────────────────────────────────────────────
const Sep = () => <span className="w-px h-6 bg-gray-300 mx-1 inline-block self-center" />;

// ── Main component ───────────────────────────────────────────────────────────
const RichTextEditor: React.FC<RichTextEditorProps> = ({
    value,
    onChange,
    placeholder = 'Type content here…',
    minHeight = 160,
    imageFolder = 'general',
}) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const isFocused = useRef(false);
    const lastHtml = useRef(value);
    const [sourceMode, setSourceMode] = useState(false);
    const [uploading, setUploading] = useState(false);
    const savedRange = useRef<Range | null>(null);

    // Sync external value changes into the editor ONLY when not focused
    useEffect(() => {
        if (!editorRef.current || isFocused.current) return;
        if (lastHtml.current === value) return;
        editorRef.current.innerHTML = value;
        lastHtml.current = value;
    }, [value]);

    // On initial mount set content
    useEffect(() => {
        if (editorRef.current && value) {
            editorRef.current.innerHTML = value;
            lastHtml.current = value;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const emitChange = useCallback(() => {
        if (!editorRef.current) return;
        const html = editorRef.current.innerHTML;
        lastHtml.current = html;
        onChange(html);
    }, [onChange]);

    // Save selection before toolbar interactions
    const saveSelection = () => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
            savedRange.current = sel.getRangeAt(0).cloneRange();
        }
    };

    const restoreSelection = () => {
        const sel = window.getSelection();
        if (sel && savedRange.current) {
            sel.removeAllRanges();
            sel.addRange(savedRange.current);
        }
    };

    const exec = (cmd: string, val?: string) => {
        editorRef.current?.focus();
        restoreSelection();
        document.execCommand(cmd, false, val);
        emitChange();
    };

    const insertLink = () => {
        saveSelection();
        const url = window.prompt('Enter URL (leave blank to use selected text as URL):', 'https://');
        if (url && url !== 'https://') {
            restoreSelection();
            exec('createLink', url);
        }
    };

    const insertImage = () => {
        saveSelection();
        const url = window.prompt('Enter image URL:', 'https://');
        if (url && url !== 'https://') {
            restoreSelection();
            exec('insertHTML', `<img src="${url}" alt="" style="max-width:100%;height:auto;border-radius:8px;margin:8px 0;" />`);
        }
    };

    // Upload image from local file → PHP backend
    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        saveSelection();
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('image', file);
            fd.append('folder', imageFolder);
            const res = await fetch(UPLOAD_URL, {
                method: 'POST',
                credentials: 'include',
                body: fd,
            });
            const data = await res.json();
            if (data.url) {
                editorRef.current?.focus();
                restoreSelection();
                exec('insertHTML', `<img src="${data.url}" alt="" style="max-width:100%;height:auto;border-radius:8px;margin:8px 0;" />`);
            }
        } catch {
            // silent
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const toggleSourceMode = () => {
        if (!editorRef.current) return;
        if (!sourceMode) {
            // switch to source: show raw HTML as text
            editorRef.current.innerText = editorRef.current.innerHTML;
        } else {
            // switch back to rich
            editorRef.current.innerHTML = editorRef.current.innerText;
            emitChange();
        }
        setSourceMode(s => !s);
    };

    return (
        <div className="border border-gray-300 rounded-xl overflow-hidden shadow-sm">
            {/* ── Toolbar ── */}
            <div className="flex flex-wrap items-center gap-0.5 p-2 bg-gray-50 border-b border-gray-200">

                {/* Font family */}
                <select
                    title="Font family"
                    onMouseDown={saveSelection}
                    onChange={e => { restoreSelection(); exec('fontName', e.target.value); }}
                    className="h-7 text-xs border border-gray-300 rounded px-1 bg-white text-gray-700 cursor-pointer hover:border-gray-400"
                >
                    {FONT_FAMILIES.map(f => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                </select>

                {/* Font size */}
                <select
                    title="Font size"
                    onMouseDown={saveSelection}
                    onChange={e => { restoreSelection(); exec('fontSize', e.target.value); }}
                    className="h-7 text-xs border border-gray-300 rounded px-1 bg-white text-gray-700 cursor-pointer hover:border-gray-400 ml-0.5"
                >
                    {FONT_SIZES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                </select>

                <Sep />

                {/* Bold / Italic / Underline / Strike */}
                <Btn title="Bold (Ctrl+B)" onClick={() => exec('bold')}><strong>B</strong></Btn>
                <Btn title="Italic (Ctrl+I)" onClick={() => exec('italic')}><em>I</em></Btn>
                <Btn title="Underline (Ctrl+U)" onClick={() => exec('underline')}><u>U</u></Btn>
                <Btn title="Strikethrough" onClick={() => exec('strikeThrough')}><s>S</s></Btn>
                <Btn title="Superscript" onClick={() => exec('superscript')}><sup className="text-xs">x²</sup></Btn>
                <Btn title="Subscript" onClick={() => exec('subscript')}><sub className="text-xs">x₂</sub></Btn>

                <Sep />

                {/* Headings */}
                <Btn title="Heading 1" onClick={() => exec('formatBlock', '<h1>')}><span className="font-extrabold text-xs">H1</span></Btn>
                <Btn title="Heading 2" onClick={() => exec('formatBlock', '<h2>')}><span className="font-bold text-xs">H2</span></Btn>
                <Btn title="Heading 3" onClick={() => exec('formatBlock', '<h3>')}><span className="font-semibold text-xs">H3</span></Btn>
                <Btn title="Heading 4" onClick={() => exec('formatBlock', '<h4>')}><span className="text-xs">H4</span></Btn>
                <Btn title="Paragraph" onClick={() => exec('formatBlock', '<p>')}><span className="text-xs">¶</span></Btn>
                <Btn title="Blockquote" onClick={() => exec('formatBlock', '<blockquote>')}><span className="text-xs font-serif">"</span></Btn>

                <Sep />

                {/* Lists */}
                <Btn title="Bullet list" onClick={() => exec('insertUnorderedList')}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                </Btn>
                <Btn title="Numbered list" onClick={() => exec('insertOrderedList')}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h13M8 12h13M8 18h13" /></svg>
                </Btn>
                <Btn title="Indent" onClick={() => exec('indent')}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 6l6 6-6 6" /><path strokeLinecap="round" strokeWidth={2} d="M4 6v12" /></svg>
                </Btn>
                <Btn title="Outdent" onClick={() => exec('outdent')}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 6l-6 6 6 6" /><path strokeLinecap="round" strokeWidth={2} d="M20 6v12" /></svg>
                </Btn>

                <Sep />

                {/* Alignment */}
                <Btn title="Align left" onClick={() => exec('justifyLeft')}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h10M4 14h16M4 18h10" /></svg>
                </Btn>
                <Btn title="Align center" onClick={() => exec('justifyCenter')}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M6 10h12M4 14h16M6 18h12" /></svg>
                </Btn>
                <Btn title="Align right" onClick={() => exec('justifyRight')}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M10 10h10M4 14h16M10 18h10" /></svg>
                </Btn>
                <Btn title="Justify" onClick={() => exec('justifyFull')}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                </Btn>

                <Sep />

                {/* Link / Unlink */}
                <Btn title="Insert / edit link" onClick={insertLink}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5" /></svg>
                </Btn>
                <Btn title="Remove link" onClick={() => exec('unlink')}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728M5.636 18.364a9 9 0 010-12.728m14.142 0L4.222 19.778" /></svg>
                </Btn>

                {/* Image — URL */}
                <Btn title="Insert image from URL" onClick={insertImage}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={2} /><circle cx="8.5" cy="8.5" r="1.5" strokeWidth={2} /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15l-5-5L5 21" /></svg>
                </Btn>

                {/* Image — Upload */}
                <label title="Upload image from your computer" className={`relative cursor-pointer flex items-center justify-center px-2 py-1 rounded text-sm transition-colors hover:bg-gray-200 text-gray-700 ${uploading ? 'opacity-50' : ''}`}>
                    {uploading ? (
                        <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M12 12V4m0 0l-3 3m3-3l3 3" /></svg>
                    )}
                    <input
                        type="file"
                        accept="image/*"
                        className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                        onChange={handleImageUpload}
                        disabled={uploading}
                    />
                </label>

                <Sep />

                {/* Text colour */}
                <label title="Text colour" className="relative cursor-pointer flex items-center justify-center w-7 h-7 rounded hover:bg-gray-200">
                    <span className="text-sm font-bold leading-none select-none text-gray-800 border-b-2 border-red-500">A</span>
                    <input type="color" className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                        onMouseDown={saveSelection}
                        onChange={e => { restoreSelection(); exec('foreColor', e.target.value); }}
                    />
                </label>

                {/* Highlight colour */}
                <label title="Highlight / background colour" className="relative cursor-pointer flex items-center justify-center w-7 h-7 rounded hover:bg-gray-200">
                    <span className="text-sm font-bold leading-none select-none px-0.5 bg-yellow-300">A</span>
                    <input type="color" defaultValue="#ffff00" className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                        onMouseDown={saveSelection}
                        onChange={e => { restoreSelection(); exec('hiliteColor', e.target.value); }}
                    />
                </label>

                <Sep />

                {/* HR */}
                <Btn title="Insert horizontal rule" onClick={() => exec('insertHorizontalRule')}>
                    <span className="text-xs">—</span>
                </Btn>

                {/* Clear formatting */}
                <Btn title="Clear all formatting" onClick={() => exec('removeFormat')}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </Btn>

                <Sep />

                {/* HTML source */}
                <Btn title={sourceMode ? 'Back to rich editor' : 'Edit raw HTML source'} onClick={toggleSourceMode} active={sourceMode}>
                    <span className="text-xs font-mono font-bold">&lt;/&gt;</span>
                </Btn>
            </div>

            {/* ── Editable area ── */}
            <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onFocus={() => { isFocused.current = true; }}
                onBlur={() => { isFocused.current = false; emitChange(); }}
                onInput={emitChange}
                onMouseUp={saveSelection}
                onKeyUp={saveSelection}
                data-placeholder={placeholder}
                className={[
                    'p-4 outline-none text-gray-800 text-sm leading-relaxed',
                    sourceMode ? 'font-mono whitespace-pre-wrap text-xs bg-gray-900 text-green-400' : 'prose prose-sm max-w-none',
                    '[&:empty]:before:content-[attr(data-placeholder)]',
                    '[&:empty]:before:text-gray-400',
                    '[&:empty]:before:pointer-events-none',
                    '[&_h1]:text-2xl [&_h1]:font-extrabold [&_h1]:my-3',
                    '[&_h2]:text-xl [&_h2]:font-bold [&_h2]:my-2.5',
                    '[&_h3]:text-lg [&_h3]:font-semibold [&_h3]:my-2',
                    '[&_h4]:text-base [&_h4]:font-semibold [&_h4]:my-1.5',
                    '[&_p]:my-1.5',
                    '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2',
                    '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2',
                    '[&_li]:my-0.5',
                    '[&_a]:text-[#EF5D22] [&_a]:underline',
                    '[&_blockquote]:border-l-4 [&_blockquote]:border-[#EF5D22] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-600',
                    '[&_hr]:border-gray-300 [&_hr]:my-4',
                    '[&_img]:max-w-full [&_img]:rounded-lg [&_img]:my-2',
                    '[&_strong]:font-bold [&_em]:italic',
                ].join(' ')}
                style={{ minHeight }}
            />
        </div>
    );
};

export default RichTextEditor;
